<template>
  <div class="d-select text-dark"
       v-on-clickaway="onClickAway"
       ref="d_select"
       :class="{'openFromBottom': openFromBottom, 'd-select--single-select': !multiple}">
    <div class="width-100 no-print"
         :class="{'box-shadow': boxShadow}">
      <div class="d-select__input-look"
           ref="toggle"
           @click="open"
           :class="{'active': showDropdown}">
        <div v-for="selectedUser in selectedUsers"
             class="d-select__selected__item"
             :class="[
               {'readyToDelete': isReadyToDelete(selectedUser)},
             {'d-select__selected__item--item-is-error': selectedUser.isErrorUser}]">
          {{ selectedUser.name }}
          <slot name="item_identifier" :userId="selectedUser.user_calendar_id"></slot>
          <template v-if="userOccEvents(selectedUser) && userOccEvents(selectedUser).length > 0">
            <div class="icon-warning item__icon-warning marg-5-left"
                 :class="{'item_icon__hide': selectedUser.occupied.events.length === 1 && selectedUser.occupied.events[0].event_type === 'presence' || selectedUser.isOnLesson && selectedUser.occupied.events.length === 1}">
            </div>
            <div class="d-select__selected__item__secret-label">
              <div class="icon-warning"></div>
              <div class="d-select__selected__item__secret-label__event"
                   v-for="(event, index) in userOccEvents(selectedUser)" v-if="index < 3">
                {{ event.title }}
              </div>
            </div>
          </template>
          <div type="button"
               v-if="isShowRemoveButton && !selectedUser.isErrorUser"
               @click.stop="removeUser(selectedUser)"
               class="d-select__selected__item__remove">
            <span>×</span>
          </div>
          <div v-else-if="!isShowRemoveButton"
          class="d-select__selected__item__menu-open">
            <span class="icon-menu-open"></span>
          </div>
        </div>
        <template v-if="isLoading">
          <spinner style="margin: 7px 0 0 0;" color="blue" size="small"></spinner>
        </template>
        <template v-else>
          <input type="search"
                 ref="inputfield"
                 v-model="searchText"
                 :placeholder="placeholderText"
                 class="d-select__header__search"
                 @input="debounceSearch"
                 :class="[{'show': showDropdown}, {'input-active': searchText.length > 0 || selectedUsers.length < 1}]"
                 @keypress="selectWithKeyboard('', $event)"
                 @keydown.enter.prevent="selectWithKeyboard('enter', $event)"
                 @keydown.down.prevent="selectWithKeyboard('down')"
                 @keydown.up.prevent="selectWithKeyboard('up')"
                 @keydown.delete="deleteUserWithKeyboard"
                 @keydown.tab="onTabKeyDown"/>
        </template>
      </div>
      <div class="d-select__items text-dark"
           :class="addClass"
           ref="d_select__items"
           v-if="showDropdown">
        <ul class="d-select__items__container"
            ref="dropdown" @scroll="handleScroll">
          <template v-for="(item, index) in searchedUsers">
            <li
              v-if="index < maxLimit"
              :key="item.user_id"
              :class="[
              {'isReadyForSelection': isSelectedWithKeyboard(item)},
              {'item_is_selected': isSelected(item)}]"
              @mouseenter="setIsReadyForSelection(item, index)"
              @click="toggleUser(item)">
              <template v-if="item.occupied && item.occupied.events.length > 0">
                <div class="item-template isOccupied"
                     :class="[
                     {'item-is-present': item.occupied.events.length === 1 && item.occupied.events[0].event_type === 'presence'},
                     {'item-is-already-attached': item.isOnLesson || item.isWithAvailability}]">
                  <slot name="occupied-item" :item="item">
                    <span v-if="item.icon" :class="item.icon" class="marg-10-right"></span>
                    <img v-if="item.default_thumb_url" :src="item.default_thumb_url" alt="">
                    <div>{{ item.name }}</div>
                    <!-- If user is occupied -->
                    <div class="warning-signs">
                      <template v-for="event in item.occupied.events">
                        <!-- If user is occupied -->
                        <template v-if="event.event_type !== 'presence'">
                          <div class="marg-10-left" v-if="item.isOnLesson && item.currentEventId === event.event_id">
                            <span class="icon-user"></span> {{ i18n['also_assigned'] }}
                          </div>
                          <div class="marg-10-left warning-signs__sign" v-show="item.currentEventId !== event.event_id">
                            <span class="icon-warning"></span> {{ event.title }}
                          </div>
                        </template>
                        <div class="marg-10-left"
                             v-show="item.occupied.events.length === 1 && event.event_type === 'presence'">
                          <span class="icon-clock"></span> {{ i18n['working_hours'] }}:
                          {{ event.event_time_start | localeTime }} -
                          {{ event.event_time_end | localeTime }}
                        </div>
                      </template>
                    </div>
                  </slot>
                </div>
                <!-- END -->
              </template>
              <div class="item-template" :class="{'item-template--space-between': itemSpaceBetween}" v-else>
                <span v-if="item.icon" :class="item.icon" class="marg-10-right"></span>
                <img v-if="item.default_thumb_url" :src="item.default_thumb_url" alt="">
                <div>{{ item.name }}</div>
                <slot name="item-badge" :item="item"></slot>
              </div>
            </li>
          </template>
          <li class="infinite-scroll__bottom" ref="infinite_scroll" v-if="searchedUsers.length > maxLimit">
            <Loader :is-extra-small="true" bg-color="#fff" style="margin: 12px"></Loader>
          </li>
        </ul>
        <div class="no-match" v-if="searchedUsers.length < 1">
          <slot name="noMatch">
            <h4>{{ $t('vue2.dselect.no_matching_results') }}</h4>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import spinner from '../common/Spinner'
import {mixin as clickaway} from 'vue-clickaway'
import moment from 'moment'
import {createPopper} from '@popperjs/core'
import Loader from '../common/Loader'

const I18N = {
  da: {
    also_assigned: 'Også tilknyttet',
    working_hours: 'Arbejdstid'
  },
  nb: {
    also_assigned: 'Også tilordnet',
    working_hours: 'Arbeidstid'
  },
  en: {
    also_assigned: 'Also assigned',
    working_hours: 'Working Hours'
  }
}

export default {
  mixins: [clickaway],
  components: {Loader, spinner},
  props: {
    items: {
      default: () => [],
      type: Array
    },
    itemsSelected: {
      default: () => [],
      type: Array
    },
    isLoading: {
      default: false,
      type: Boolean
    },
    allDataLoaded: {
      default: false,
      type: Boolean
    },
    show: {
      default: false,
      type: Boolean
    },
    multiple: {
      default: true,
      type: Boolean
    },
    placeholder: {
      type: String,
      default: ''
    },
    eventId: {
      type: Number,
      required: false
    },
    receiveData: {},
    showSelectedUsers: {
      default: true,
      type: Boolean
    },
    addClass: {
      default: () => [],
      type: Array
    },
    itemSpaceBetween: {
      type: Boolean,
      default: false
    },
    resetCounter: {
      default: 0
    },
    selectedSubject: {
      default: null
    },
    isShowRemoveButton: {
      default: true,
      type: Boolean
    }
  },
  computed: {
    withoutSelectedItems () {
      return this.items.filter(el => {
        return this.selectedUsers.filter(secEl => {
          return secEl.id === el.id
        }).length === 0
      })
    },
    searchedUsers () {
      return this.withoutSelectedItems.filter(user => {
        const nameMatch = user.name !== '' ? user.name.match(new RegExp(this.searchText, 'i')) : ''
        const initialsMatch = user.initials && user.initials !== '' ? user.initials.match(new RegExp(this.searchText, 'i')) : ''
        return nameMatch || initialsMatch
      })
    },
    placeholderText () {
      let placeholder = this.placeholder.length > 0 ? this.placeholder : this.$i18n.t('vue2.dselect.placeholder')
      return this.selectedUsers.length > 0 ? '' : placeholder
    },
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    }
  },
  data () {
    return {
      showDropdown: false,
      searchText: '',
      searchTextDebounced: '',
      selectedUsers: [],
      keyboardSelected: null,
      keyboardSelectedIndex: 0,
      readyToDelete: null,
      openFromBottom: false,
      maxLimit: 400,
      addLimit: 400,
      debounceTimer: null
    }
  },
  watch: {
    selectedSubject: {
      immediate: true,
      handler (val) {
        if (!val || !val.id || !Array.isArray(this.items)) {
          return
        }
        const found = this.items.find(item => item.id === val.id)
        if (found) {
          this.toggleUser(found)
        }
      }
    },
    // For creating absent reason
    receiveData (val) {
      for (let i = 0; i < this.items.length; i++) {
        if (this.receiveData.id === this.items[i].id) {
          this.toggleUser(this.items[i])
        }
      }
      this.searchText = ''
    },
    resetCounter (val) {
      this.searchText = ''
      this.selectedUsers = []
    },
    // Close from from component
    show () {
      if (!this.show) {
        this.close()
      }
    },
    searchedUsers (newVal) {
      if (this._preserveKeyboardIndex) {
        this._preserveKeyboardIndex = false
        if (this.keyboardSelectedIndex >= newVal.length) {
          this.keyboardSelectedIndex = Math.max(0, newVal.length - 1)
        }
        this.keyboardSelected = newVal[this.keyboardSelectedIndex] || null
      } else {
        this.keyboardSelectedIndex = 0
        this.keyboardSelected = newVal[0]
      }
    },
    searchText () {
      this.$emit('searchText', this.searchText)
    },
    allDataLoaded () {
      this.sortSelectedUsers()
    }
  },
  filters: {
    localeTime (eventTime) {
      return moment(eventTime).format('LT')
    }
  },
  methods: {
    onTabKeyDown () {
      this.close()
    },
    debounceSearch (event) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.searchTextDebounced = event.target.value
        this.$emit('onSearchTextDebounced', this.searchTextDebounced)
      }, 400)
    },
    createPopper () {
      let source = this.$refs.d_select__items
      let el = this.$refs.d_select
      document.getElementById('djs-content-container').appendChild(source)
      let elWidth = el.getBoundingClientRect().width
      source.style.width = elWidth + 'px'
      createPopper(el, source, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0]
            }
          }
        ]
      })
    },
    userOccEvents (selectedUser) {
      if (selectedUser.occupied && selectedUser.occupied.events.length > 0) {
        let occEventsWithoutPresenceAndCurrentEvent = selectedUser.occupied.events.filter(lesson => lesson.event_id !== this.eventId && lesson.event_type !== 'presence')
        return occEventsWithoutPresenceAndCurrentEvent
      } else {
        return false
      }
    },
    checkHeight () {
      const bcr = this.$el.getBoundingClientRect()
      const pxFromTop = bcr.top
      const pxFromBottom = window.innerHeight - (bcr.top + bcr.height)
      if (pxFromBottom < 330 && pxFromTop > 330) {
        this.openFromBottom = true
      }
    },
    isReadyToDelete (user) {
      if (!this.readyToDelete) {
        return
      }
      return this.readyToDelete.id === user.id
    },
    scrollTokeyboardSelected () {
      setTimeout(() => {
        let user = document.querySelector('.isReadyForSelection')
        let selectedOffsetTop = user.offsetTop
        let selectedHeight = user.getBoundingClientRect().height
        let totalHeight = selectedOffsetTop + selectedHeight

        let top = this.$refs.dropdown.scrollTop
        let height = this.$refs.dropdown.getBoundingClientRect().height
        let bottom = top + height

        if (totalHeight > bottom) {
          this.$refs.dropdown.scrollTop = this.$refs.dropdown.scrollTop + selectedHeight
        }

        if (selectedOffsetTop < top) {
          this.$refs.dropdown.scrollTop = this.$refs.dropdown.scrollTop - selectedHeight
        }
      }, 0)
    },
    setIsReadyForSelection (user, idx) {
      this.keyboardSelected = null
      this.keyboardSelected = user
      this.keyboardSelectedIndex = idx
    },
    selectWithKeyboard (stroke, e) {
      // Open Dropdown if closed and typing
      if (!this.showDropdown) {
        this.open()
      }
      if (stroke === 'down') {
        if ((this.keyboardSelectedIndex + 1) >= this.searchedUsers.length) {
          return
        }
        this.keyboardSelectedIndex = this.keyboardSelectedIndex + 1
        this.keyboardSelected = this.searchedUsers[this.keyboardSelectedIndex]
        this.scrollTokeyboardSelected()
      }
      if (stroke === 'up') {
        if ((this.keyboardSelectedIndex - 1) < 0) {
          return
        }
        this.keyboardSelectedIndex = this.keyboardSelectedIndex - 1
        this.keyboardSelected = this.searchedUsers[this.keyboardSelectedIndex]
        this.scrollTokeyboardSelected()
      }
      if (stroke === 'enter') {
        if (this.keyboardSelected === null) {
          return
        }
        if (this.searchedUsers.length < 1) {
          this.$emit('enter')
          return
        }
        let keepOpen = e && (e.metaKey || e.ctrlKey)
        this._preserveKeyboardIndex = true
        this.toggleUser(this.keyboardSelected, keepOpen)
      }
    },
    isSelectedWithKeyboard (user) {
      return this.keyboardSelected && this.keyboardSelected.id === user.id
    },
    deleteUserWithKeyboard () {
      if (this.searchText.length > 0 || this.selectedUsers.length < 1) {
        return
      }
      if (this.readyToDelete === null) {
        this.readyToDelete = this.selectedUsers[this.selectedUsers.length - 1]
      } else {
        this.removeUser(this.selectedUsers[this.selectedUsers.length - 1])
        this.readyToDelete = null
      }
    },
    onClickAway () {
      this.close()
      this.$emit('close')
    },
    close () {
      if (!this.showDropdown) {
        return
      }
      this.showDropdown = false
      this.keyboardSelected = null
      this.keyboardSelectedIndex = 0
      this.readyToDelete = null
      this.searchText = ''
      this.$emit('show', false)
      this.$emit('close')
      this.openFromBottom = false
      this.maxLimit = this.addLimit
    },
    open () {
      this.checkHeight()
      this.showDropdown = true
      this.$emit('show', true)
      this.setFocus()
      this.keyboardSelectedIndex = 0
      this.$nextTick(_ => {
        this.createPopper()
      })
    },
    toggleUser (item, keepOpen) {
      if (item.tag) {
        this.selectAllWithTag(item.name)
        return
      }
      if (this.isSelected(item)) {
        this.selectedUsers.splice(this.selectedUsers.indexOf(item), 1)
        this.$emit('remove', item)
        this.$emit('update', this.selectedUsers)
      } else {
        if (item.isOnLesson) {
          this.$emit('addIsOnLesson', item)
          if (!keepOpen) this.close()
          return
        }
        if (this.multiple) {
          this.selectedUsers.push(item)
          this.$emit('add', item)
        } else {
          this.selectedUsers = []
          this.selectedUsers.push(item)
          this.$emit('add', item)
        }
        this.$emit('update', this.selectedUsers)
      }
      if (!this.showSelectedUsers) {
        this.selectedUsers = []
      }
      if (!keepOpen) {
        this.close()
      }
    },
    selectAllWithTag (tag) {
      let usersWithTag = this.items.filter(item => {
        return item.user_tags && item.user_tags.indexOf(tag) > -1
      })
      usersWithTag.forEach(user => {
        let idx = this.items.findIndex(x => x.id === user.id)
        if (!this.isSelected(this.items[idx])) {
          this.selectedUsers.push(this.items[idx])
        }
      })
      this.close()
      this.$emit('update', this.selectedUsers)
    },
    removeUser (item) {
      this.selectedUsers.splice(this.selectedUsers.indexOf(item), 1)
      this.$emit('remove', item)
      this.$emit('update', this.selectedUsers)
      this.close()
    },
    isSelected (user) {
      return this.selectedUsers.indexOf(user) > -1
    },
    setFocus () {
      this.$refs.inputfield.focus()
    },
    sortSelectedUsers () {
      this.selectedUsers = this.selectedUsers.sort((a, b) => {
        var x = a.name.toLowerCase()
        var y = b.name.toLowerCase()
        return x < y ? -1 : x > y ? 1 : 0
      })
    },
    loadMoreCalendars () {
      if (this.searchedUsers.length > this.maxLimit) {
        this.maxLimit = this.maxLimit + this.addLimit
      }
    },
    handleScroll (evt) {
      let element = evt.target
      // scrolledToBottom
      if (element.scrollHeight - element.scrollTop === element.clientHeight) {
        this.loadMoreCalendars()
      }
    }
  },
  mounted () {
    this.$nextTick(_ => {
      if (this.show) {
        this.open()
      }
    })
    this.selectedUsers = this.itemsSelected
  },
  beforeDestroy () {
    // remove the dropdown from the DOM
    let source = this.$refs.d_select__items
    if (source) {
      source.remove()
    }
  }
}
</script>


<style scoped>
@import '../../assets/css/variables.css';

.d-select {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 30px;
}

.d-select__header__search {
  width: 100%;
  transition: width 0.25s;
  border-radius: 4px;
}

.d-select__input-look {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  border-radius: 4px;
  cursor: text;
  border: 1px solid #edf2f7;
  -webkit-appearance: none !important;
  background: #fff;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
  box-sizing: border-box !important;
  transition: background-color .24s, box-shadow .24s;
}

.d-select__input-look:focus-within {
  border: 1px solid var(--blue);
}

.d-select__input-look.active {
  border: 1px solid var(--blue);
  border-radius: 4px 4px 0 0;
}

.d-select.openFromBottom .d-select__input-look.active {
  border-radius: 0 0 4px 4px;
}

.d-select__input-look input[type="search"] {
  border: none !important;
  width: 20px;
  box-shadow: none;
}

.d-select__input-look input[type="search"].input-active {
  width: 200px;
}

.d-select__selected__item {
  display: flex;
  position: relative;
  align-items: center;
  box-sizing: border-box;
  width: max-content;
  color: #333;
  border: 1px solid #c8d6e2;
  background: #f6f9fc;
  border-radius: 4px;
  height: 26px;
  margin: 4px 1px 4px 3px;
  padding: 1px .25em;
  float: left;
  line-height: 24px;
}

.d-select__selected__item.readyToDelete {
  background-color: var(--danger);
}

.d-select__selected__item__remove,
.d-select__selected__item__menu-open {
  float: none;
  margin-right: 0;
  font-size: 20px;
  margin-left: 5px;
  appearance: none;
  padding: 0;
  cursor: pointer;
  background: 0 0;
  border: 0;
  font-weight: 700;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: .2;
}

.d-select__selected__item__remove:hover {
  opacity: 1;
}

.d-select__selected__item__menu-open {
  font-size: 12px;
}

.d-select__items {
  background-color: #ffffff;
  z-index: 123123123123;
  padding: 0;
  width: 100%;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid var(--blue);
  border-width: 0 1px 1px 1px;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 14px 0 rgba(0, 0, 0, .2), 0 0 0 1px rgba(0, 0, 0, .05);
}

.d-select.openFromBottom .d-select__items {
  top: auto;
  bottom: 100%;
  border-width: 1px 1px 0px 1px;
  border-radius: 4px 4px 0 0;
}

.d-select__items__container {
  max-height: 50vh;
  overflow-y: auto;
  margin: 0;
}

.d-select__items h4 {
  padding: 5px 15px;
}

.d-select__items li.isReadyForSelection {
  background-color: var(--blue);
  color: white;
}

.item_is_selected {
  background-color: #cccccc;
}

.item-template {
  display: flex;
  align-items: center;
  border-left: 2px solid transparent;
  padding: 5px 15px;
  cursor: pointer;
}

.item-template.item-template--space-between {
  justify-content: space-between;
}

.item-template > img {
  height: 18px;
  margin-right: 10px;
}

.item-template.isOccupied {
  background-color: rgba(251, 127, 152, 0.5);
  border-left: 2px solid #fb7f98;
  color: black;
}

.item-template.isOccupied.item-is-present {
  background-color: rgba(153, 197, 244, 0.5);
  border-left: 2px solid #99c5f4;
  color: black;
}

.item-template.isOccupied.item-is-already-attached {
  background-color: rgba(136, 209, 170, 0.5);
  border-left: 2px solid #4CAF50;
  color: black;
}

.no-match {
  background: #f4f4f4;
  border-radius: 0 0 4px 4px;
}

.warning-signs {
  display: flex;
  align-items: center;
}

warning-signs__sign {
  margin-right: 10px;
  display: flex;
}

.warning-sign > span {
  margin-right: 5px;
}

.item__icon-warning {
  display: block;
  padding: 2px 4px;
  font-size: 10px;
  background-color: rgba(251, 127, 152, 0.5);
  border-left: 2px solid #fb7f98;
  color: black;
}

.item_icon__hide {
  display: none !important;
}

.d-select__selected__item__secret-label {
  display: none !important;
  position: absolute;
  z-index: 100;
  align-items: center;
  bottom: calc(100%);
  left: calc(100% - 39px);
  font-size: 10px;
  padding: 0 6px 0 4px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, .3);
  background-color: #fdc4cf;
  border-left: 2px solid #fb7f98;
  color: black;
}

.item__icon-warning:hover + .d-select__selected__item__secret-label {
  display: flex !important;
  transform: translateY(2px);
  transition: all 1s;
}

.d-select__selected__item__secret-label__event {
  display: flex;
  margin-left: 2px;
}

.d-select__selected__item__secret-label__event:after {
  content: ',';
}

.d-select__selected__item__secret-label__event:last-child:after {
  content: '';
}

.d-select__selected__item--item-is-error {
  color: #FA565A;
  font-style: italic;
}

.small-font {
  font-size: 11px;
}

.d-select--single-select .d-select__input-look {
  display: grid;
  align-items: center;
  padding: 0 8px;
}

.d-select--single-select .d-select__input-look .d-select__selected__item {
  grid-area: 1 / 1 / 2 / 3;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
}
.d-select--single-select .d-select__input-look .d-select__header__search {
  position: relative;
  z-index: 2;
  width: 5px;
  margin-top: 0;
  transition: none;
  background: transparent;
  padding: 9px 0;
  grid-area: 1 / 1 / 2 / 3;
}

.d-select--single-select .d-select__input-look input[type="search"].input-active.d-select__header__search {
  width: 100%;
  background-color: #fff;
}

</style>



// WEBPACK FOOTER //
// DSelect.vue?313d5fd2